import type {
  ApprovalMode,
  ApprovalRequestSchema,
  ApprovalScope,
  ApprovalStatus,
  ApprovalStepStatus,
} from '../types/approval.type';

const STATUS_LABELS: Record<ApprovalStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  executed: 'Completed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

const STATUS_COLORS: Record<
  ApprovalStatus,
  'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'
> = {
  pending: 'warning',
  approved: 'info',
  executed: 'success',
  rejected: 'error',
  cancelled: 'default',
};

const MODE_LABELS: Record<ApprovalMode, string> = {
  justified: 'Auto-approved',
  requires_approval: 'Needs review',
};

const STEP_STATUS_LABELS: Record<ApprovalStepStatus, string> = {
  pending: 'Awaiting review',
  approved: 'Approved',
  rejected: 'Rejected',
};

const ACTION_TYPE_LABELS: Record<string, string> = {};

export function approvalStatusLabel(status: ApprovalStatus): string {
  return STATUS_LABELS[status] ?? status;
}

export function approvalStatusColor(
  status: ApprovalStatus,
): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' {
  return STATUS_COLORS[status] ?? 'default';
}

export function approvalModeLabel(mode: ApprovalMode): string {
  return MODE_LABELS[mode] ?? mode;
}

export function approvalModeColor(mode: ApprovalMode): 'default' | 'info' | 'secondary' {
  return mode === 'requires_approval' ? 'info' : 'secondary';
}

export function approvalStepStatusLabel(status: ApprovalStepStatus): string {
  return STEP_STATUS_LABELS[status] ?? status;
}

export function approvalStepStatusColor(
  status: ApprovalStepStatus,
): 'default' | 'success' | 'warning' | 'error' {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'warning';
  }
}

export function approvalScopeLabel(scope: ApprovalScope): string {
  return scope === 'platform' ? 'Platform' : 'Organization';
}

export function approvalActionTypeLabel(actionType: string): string {
  if (ACTION_TYPE_LABELS[actionType]) {
    return ACTION_TYPE_LABELS[actionType];
  }
  return actionType
    .split('.')
    .map((part) => part.replace(/_/g, ' '))
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' · ');
}

export function entityTypeLabel(entityType: string): string {
  return entityType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function approvalUserDisplay(
  username?: string | null,
  userId?: string | null,
): string {
  if (username?.trim()) return username.trim();
  if (userId && userId.length > 10) return `User ${userId.slice(0, 8)}…`;
  return userId ?? 'Unknown';
}

export function approvalUserInitial(username?: string | null, userId?: string | null): string {
  const label = approvalUserDisplay(username, userId);
  return label.charAt(0).toUpperCase();
}

export function formatApprovalDate(value?: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return value;
  }
}

export function formatApprovalDateShort(value?: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

export function approvalSubmittedAt(row: ApprovalRequestSchema): string | null | undefined {
  return row.requested_at ?? row.created_at;
}

export function approvalOutcomeSummary(row: ApprovalRequestSchema): string | null {
  if (row.status === 'rejected') {
    return row.resolved_by_username
      ? `Rejected by ${row.resolved_by_username}`
      : 'Rejected';
  }
  if (row.status === 'cancelled') {
    return row.resolved_by_username
      ? `Cancelled by ${row.resolved_by_username}`
      : 'Cancelled';
  }
  if (row.status === 'executed' || row.status === 'approved') {
    if (row.resolved_by_username) return `Approved by ${row.resolved_by_username}`;
    if (row.mode === 'justified') return 'Auto-approved';
  }
  return null;
}

export function getApprovalNotificationPath(data: Record<string, unknown> | null | undefined): string | null {
  if (!data || data.notification_kind !== 'approval') return null;
  const id = data.approval_request_id;
  if (!id || typeof id !== 'string') return null;
  return `/approvals?request=${encodeURIComponent(id)}`;
}

export function isApprovalNotification(data: Record<string, unknown> | null | undefined): boolean {
  return data?.notification_kind === 'approval';
}

export interface ApprovalCurrentUser {
  sub?: string | null;
  id?: string | null;
  username?: string | null;
}

/** True when the logged-in user submitted the request (matches sub or profile id). */
export function isApprovalSubmitter(
  requestedBy: string | null | undefined,
  currentUser: ApprovalCurrentUser,
): boolean {
  if (!requestedBy) return false;
  const normalizedRequested = requestedBy.trim().toLowerCase();
  const candidates = [currentUser.sub, currentUser.id]
    .filter((value): value is string => Boolean(value))
    .map((value) => value.trim().toLowerCase());
  return candidates.some((candidate) => candidate === normalizedRequested);
}
